import { useState, useEffect, useCallback } from "react";
import { useLoadScript } from "@react-google-maps/api";
import AddressMap from "./AddressMap";
import AddressFormFields from "./AddressFormFields";
import { addUserAddress } from "../../../services/addressApi/addressApi";
import { useLocationContext } from "../../../context/LocationContext";

const LIBRARIES = ["places"];

export default function AddressAddForm({
  userId,
  addressTypes = [], // ✅ Default empty array added
  onSuccess,
  onCancel,
}) {
  const apiKey = "AIzaSyAlqS1FMKTkCJoaOHzghrXA_6PA6hflHCo";
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries: LIBRARIES,
  });

  const { updateLocation } = useLocationContext();

  const [form, setForm] = useState({
    AddressTitle: "",
    FaltHousNumber: "",
    Street: "",
    Locality: "",
    Pincode: "",
    AddressTypeID: 1,
  });

  const [position, setPosition] = useState({
    lat: 28.6139,
    lng: 77.209,
  });

  const [loading, setLoading] = useState(false);

  /* ================= Reverse Geocode ================= */
  const reverseGeocode = async (lat, lng) => {
    try {
      const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`;
      const res = await fetch(url);
      const data = await res.json();
      if (!data.results?.length) return "";
      return data.results[0].formatted_address;
    } catch (err) {
      console.error("Geocode error", err);
      return "";
    }
  };

  /* ================= Location Select ================= */
  const onSelectLocation = useCallback(async (lat, lng) => {
    setPosition({ lat, lng });
    setLoading(true);

    const address = await reverseGeocode(lat, lng);
    const pincode = address.match(/\b\d{6}\b/)?.[0] || "";
    const parts = address.split(",");

    const newForm = {
      AddressTitle: "Selected Location",
      FaltHousNumber: "",
      Street: parts[0]?.trim() || "",
      Locality: parts.slice(1, 3).join(", ").trim() || "",
      Pincode: pincode,
      AddressTypeID: 1,
    };

    setForm(newForm);

    const fullAddress = [
      newForm.FaltHousNumber,
      newForm.Street,
      newForm.Locality,
      newForm.Pincode,
    ]
      .filter(Boolean)
      .join(", ");
    updateLocation(fullAddress, { lat, lng });

    setLoading(false);
  }, [updateLocation]);

  /* ================= Live Location on Mount ================= */
  useEffect(() => {
    const fetchLiveLocation = async () => {
      if (!navigator.geolocation) {
        alert("Geolocation not supported");
        onSelectLocation(28.6139, 77.209);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          console.log("✅ LIVE location success:", pos.coords);
          onSelectLocation(pos.coords.latitude, pos.coords.longitude);
        },
        (err) => {
          console.error("❌ LIVE location error:", err);
          alert("Live location failed, using default location.");
          onSelectLocation(28.6139, 77.209);
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    };

    fetchLiveLocation();
  }, [onSelectLocation]);

  /* ================= Submit ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      id: 0,
      addressTypeID: Number(form.AddressTypeID),
      userId: Number(userId),
      addressTitle: form.AddressTitle,
      faltHousNumber: form.FaltHousNumber,
      street: form.Street,
      locality: form.Locality,
      pincode: form.Pincode,
      isDefault: true,
    };

    try {
      const response = await addUserAddress(payload);
      alert(response.message);

      if (response.succeeded) {
        const fullAddress = [
          form.FaltHousNumber,
          form.Street,
          form.Locality,
          form.Pincode,
        ]
          .filter(Boolean)
          .join(", ");
        updateLocation(fullAddress, position);
        onSuccess();
      }
    } catch (error) {
      console.error("API Error", error);
      alert("Something went wrong!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">

      {/* ── HEADER ── */}
      <div
        className="flex items-center gap-3 px-6 py-4 border-b border-gray-100"
        style={{ backgroundColor: "#fff5f5" }}
      >
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: "#fee2e2" }}
        >
          <span style={{ fontSize: 18 }}>📍</span>
        </div>
        <div>
          <h2
            className="m-0 text-base font-semibold"
            style={{ color: "#111827" }}
          >
            Add New Address
          </h2>
          <p className="m-0 text-xs" style={{ color: "#9ca3af" }}>
            Pin your location on the map
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} style={{ padding: "20px" }}>

        {/* ── MAP ── */}
        <div
          className="rounded-xl overflow-hidden mb-5"
          style={{ border: "1px solid #e5e7eb" }}
        >
          {isLoaded ? (
            <AddressMap position={position} onSelect={onSelectLocation} />
          ) : (
            <div
              className="flex items-center justify-center"
              style={{ height: 192, backgroundColor: "#f9fafb" }}
            >
              <div className="flex items-center gap-2" style={{ color: "#9ca3af", fontSize: 14 }}>
                <svg className="animate-spin" style={{ width: 16, height: 16 }} fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                Loading map...
              </div>
            </div>
          )}
        </div>

        {/* ── FORM FIELDS ── */}
        <div style={{ marginBottom: 20 }}>
          <AddressFormFields
            form={form}
            setForm={setForm}
            addressTypes={addressTypes}
          />
        </div>

        {/* ── BUTTONS ── */}
        <div
          className="flex gap-3"
          style={{ paddingTop: 16, borderTop: "1px solid #f3f4f6" }}
        >
          {/* SAVE */}
          <button
            type="submit"
            disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 rounded-xl"
            style={{
              backgroundColor: loading ? "#f87171" : "#dc2626",
              color: "#ffffff",
              fontSize: 14,
              fontWeight: 600,
              padding: "11px 24px",
              border: "none",
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.75 : 1,
              transition: "background-color 0.15s",
            }}
          >
            {loading ? (
              <>
                <svg
                  className="animate-spin"
                  style={{ width: 16, height: 16, color: "#ffffff" }}
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                <span style={{ color: "#ffffff" }}>Saving...</span>
              </>
            ) : (
              <span style={{ color: "#ffffff" }}>Save Address</span>
            )}
          </button>

          {/* CANCEL */}
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 rounded-xl"
            style={{
              backgroundColor: "#ffffff",
              color: "#374151",
              fontSize: 14,
              fontWeight: 600,
              padding: "11px 24px",
              border: "1px solid #e5e7eb",
              cursor: "pointer",
              transition: "background-color 0.15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f9fafb")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#ffffff")}
          >
            <span style={{ color: "#374151" }}>Cancel</span>
          </button>
        </div>

      </form>
    </div>
  );
}